import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ReactComponent as SettingIcon } from '../../assets/icons/setting-icon.svg';
import { useAppBranding } from '../../config/branding';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import PageTitle from '../../shared-components/page-title/PageTitle';
import TabToggle from '../../shared-components/tab-toggle/TabToggle';
import SidePaneModel from '../../shared-components/table-side-panel/SidePaneModel';
// import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import penaltyManagementApi from '../../utils/api/penalty-management';

import GlobalSetting from './components/GlobalSetting';
// import AccountListPenalty from './components/PenaltyAccountList';
import RequestList from './components/request/RequestList';
import TransactionList from './components/TransactionList';

interface SidePaneInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

interface DurationAmountListInterface {
  from: number;
  to: number;
  mode: string;
  amount: number;
}

interface QuickViewDataInterface {
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveTo: string;
  dynamicPricing: string[];
  durationAmountList: DurationAmountListInterface[];
}

interface CardDataInterface {
  totalPenaltyAmount: number;
  count: number;
  totalBaseAmount: number;
}

/**
 * Universal text formatter for UI
 */
const formatText = (value?: string | string[]) => {
  if (!value) return '';

  const text = Array.isArray(value) ? value.join(', ') : value;

  return (
    text
      // replace snake_case & kebab-case
      .replace(/[_-]/g, ' ')
      // add space before capital letters (camelCase / PascalCase)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // ensure space after commas
      .replace(/,/g, ', ')
      // capitalize each word
      .replace(/\b\w/g, (char) => char.toUpperCase())
      // remove extra spaces
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * Penalty Management pages
 * @returns
 */
function PenaltyManagement() {
  const { t, i18n } = useTranslation('penaltyManagement');

  const branding = useAppBranding();

  /**
   * Get localized label for ceiling type
   * @param value - The ceiling type value from backend
   * @returns string
   */
  const getCeilingTypeLabel = (value?: string) => {
    if (value === 'fixedAmount') return t('fixedAmount');
    if (value === 'percentage') return t('percentageOfBase');
    return formatText(value);
  };

  /**
   * Get localized label for trigger message
   * @param value - The trigger message value from backend
   * @returns string
   */
  const getTriggerMessageLabel = (value?: string) => {
    if (value === 'SuspendedEVSE') return t('suspendedEvse');
    if (value === 'SuspendedEV') return t('suspendedEv');
    if (value === 'Faulted') return t('faulted');
    if (value === 'Finishing') return t('finishing');
    return formatText(value);
  };

  /**
   * Get localized label for dynamic pricing
   * @param value - The dynamic pricing mode from backend
   * @returns string
   */
  const getDynamicPricingLabel = (value?: string) => {
    if (value === 'base') return t('base');
    if (value === 'peak') return t('peak');
    if (value === 'offpeak') return t('offPeak');
    return formatText(value);
  };

  /**
   * Get localized label for mode
   * @param value - The penalty mode from backend
   * @returns string
   */
  const getModeLabel = (value?: string) => {
    if (value === 'grace') return t('grace');
    if (value === 'flatRate') return t('flatRate');
    if (value === 'perMinute') return t('perMinute');
    return formatText(value);
  };

  const dispatch = useAppDispatch();
  const [isSidePaneOpen, setIsSidePaneOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { currentPage } = useParams<{ currentPage: string }>();
  const [openTab, setOpenTab] = useState<number>(1);
  const [quickViewData, setQuickViewData] = useState<QuickViewDataInterface | null>(null);
  const [cardData, setCardData] = useState<CardDataInterface>();
  const [SidePaneView, setSidePaneView] = useState<SidePaneInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: 'ok',
    notification: false,
  });

  const durationList = quickViewData?.durationAmountList || [];
  const firstDuration = durationList[0];
  const remainingDurations = durationList.slice(1);

  /**
   * Fetch global setting quick view data
   */
  const fetchData = async () => {
    try {
      const response = await penaltyManagementApi.globalSettingQuickView();
      setQuickViewData(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  // Set tab based on URL
  useEffect(() => {
    if (currentPage) {
      let number = 0;
      switch (currentPage) {
        case 'approved':
          number = 0;
          break;
        case 'unapproved':
          number = 1;
          break;
        case 'rejected':
          number = 2;
          break;
        default:
          number = 0;
      }
      setOpenTab(number);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Change tab + update URL
   * @param tabLabel
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('request'), t('transaction')].indexOf(tabLabel);
    setOpenTab(index);

    let path = '';

    switch (index) {
      // case 0:
      //   path = 'account';
      //   break;
      case 0:
        path = 'request';
        break;
      case 1:
        path = 'transaction';
        break;
      default:
        path = 'request';
    }

    window.history.pushState({}, '', path);
  };

  /**
   * Modal box close function
   */
  const closeSidePane = () => {
    // if (message) {
    //   setIsSidePaneOpen(true);
    //   setSidePaneView({
    //     title: '',
    //     header: false,
    //     content: (
    //       <SuccessNotification
    //         title={message}
    //         description=""
    //         notificationCloseTimer={3000}
    //         onNotificationClose={() => {
    //           setIsSidePaneOpen(false);
    //         }}
    //       />
    //     ),
    //     rightCorner: '',
    //     notification: true,
    //   });
    // } else {
    setIsSidePaneOpen(false);
    // }
  };

  /**
   * AddOverrides
   */
  const handleGlobalSetting = () => {
    setIsSidePaneOpen(true);
    setSidePaneView({
      title: t('accountSettings'),
      header: false,
      content: <GlobalSetting functionCall={fetchData} closeModal={closeSidePane} />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Fetching card data
   */
  const fetchCardData = async () => {
    try {
      const response = await penaltyManagementApi.fetchCardData();
      setCardData(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('idleFee'),
        icon: <SettingIcon />,
        count: undefined,
      }),
    );
  }, []);

  return (
    <div className="penalty-management-main" dir={i18n.dir()}>
      <SidePaneModel
        open={isSidePaneOpen}
        title={SidePaneView.title}
        content={SidePaneView.content}
        closePanel={() => setIsSidePaneOpen(false)}
      />
      <PageTitle
        title=""
        buttonLabel={t('accountSettings')}
        buttonSvgIcon={<SettingIcon />}
        buttonType="none"
        onClickButton={handleGlobalSetting}
      />

      <div className="revenue-content">
        <div className="revenue-table">
          <p className="title">{t('baseRevenue')}</p>
          <div className="value-content">
            <p className="value">{ToFixedValue(cardData?.totalBaseAmount)}</p>
            <branding.icons.currencyIcon className="icon" />
          </div>
        </div>{' '}
        <div className="revenue-table">
          <p className="title">{t('penaltyRevenue')}</p>
          <div className="value-content">
            <p className="value">{ToFixedValue(cardData?.totalPenaltyAmount)}</p>
            <branding.icons.currencyIcon className="icon" />
          </div>
        </div>{' '}
        <div className="revenue-table">
          <p className="title">{t('totalRevenue')}</p>
          <div className="value-content">
            <p className="value">
              {ToFixedValue(
                Number(cardData?.totalBaseAmount || 0) + Number(cardData?.totalPenaltyAmount || 0),
              )}
            </p>
            <branding.icons.currencyIcon className="icon" />
          </div>
        </div>
      </div>

      <div className="global-settings-quick-view">
        <p className="title">{t('globalSettingsQuickView')}</p>
        <div className="quick-view">
          <div className="quick-view-item">
            <span className="label">{t('ceilingType')}</span>
            <span className="value">
              {getCeilingTypeLabel(quickViewData?.ceilingType) || t('na')}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('maxCeilingValue')}</span>
            <span className="value">{quickViewData?.ceilingValue ?? 0}</span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('triggerMessage')}</span>
            <span className="value">
              {quickViewData?.triggerMessage?.map(getTriggerMessageLabel).join(', ') || t('na')}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('effectiveFrom')}</span>
            <span className="value">
              {quickViewData?.effectiveFrom
                ? ConvertUTCtoDate(quickViewData?.effectiveFrom)
                : t('na')}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('effectiveTo')}</span>
            <span className="value">
              {quickViewData?.effectiveTo ? ConvertUTCtoDate(quickViewData?.effectiveTo) : t('na')}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('dynamicPricing')}</span>
            <span className="value">
              {quickViewData?.dynamicPricing?.map(getDynamicPricingLabel).join(', ') || t('na')}
            </span>
          </div>

          <div className="duration-dropdown">
            {/* Trigger */}
            <div
              role="presentation"
              className={`duration-trigger ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen((previous) => !previous)}>
              <div className="row">
                <div className="each-row">
                  <span className="label">{t('fromMins')}</span>
                  <span className="value">{firstDuration?.from ?? 0}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('toMins')}</span>
                  <span className="value">{firstDuration?.to ?? 0}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('mode')}</span>
                  <span className="value">{getModeLabel(firstDuration?.mode) || t('na')}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('amount')}</span>
                  <span className="value">
                    {firstDuration?.amount ?? 0} {t(branding.text.currency)}
                  </span>
                </div>
              </div>

              <span className={`arrow ${isOpen ? 'open' : ''}`} />
            </div>

            {/* Dropdown */}
            {isOpen && remainingDurations.length > 0 && (
              <div className="duration-menu">
                {remainingDurations.map((item) => (
                  <div className="menu-row" key={item.from}>
                    <div className="each-row">
                      <span className="label">{t('fromMins')}</span>
                      <span className="value">{item.from}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('toMins')}</span>
                      <span className="value">{item.to}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('mode')}</span>
                      <span className="value">{getModeLabel(item.mode)}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('amount')}</span>
                      <span className="value">
                        {item.amount} {t(branding.text.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <TabToggle
        tabs={[t('request'), t('transaction')]}
        defaultTab={t('request')}
        onChange={(tab) => changeTabPages(tab)}
      />
      <div className="table-content">
        {/* {openTab === 0 && (
          <div>
            <AccountListPenalty />
          </div>
        )} */}
        {openTab === 0 && (
          <div>
            <RequestList />
          </div>
        )}
        {openTab === 1 && (
          <div>
            <TransactionList />
          </div>
        )}
      </div>
    </div>
  );
}

export default PenaltyManagement;
